/**
 * 视频「成片编辑」面板纯逻辑(无 DOM,可单测)。
 * 字段名对齐后端 VideoComposeOptions;overlay 复用 @/shared 的 OverlayConfig。
 */
import { pickDefault, type BrandAssets } from "@/entities/brand-asset";
import { DEFAULT_OVERLAY, type OverlayConfig } from "@/shared/lib/overlay/overlayConfig";
import type { VideoAssetDto, VideoComposeOptions, VideoTaskDto } from "../api/videoService";

export interface ComposeToggles {
  voiceEnabled: boolean;
  subtitleEnabled: boolean;
  bgmEnabled: boolean;
}

/** 本片有无可切素材 —— 决定开关是否可用。 */
export interface ComposeMaterials {
  hasVoice: boolean; // 有非空口播文案 → 可开关 声音 & 字幕(字幕由口播文案烧录)
  hasBgm: boolean; // 选了 BGM(平台 id 或自传 key)→ 可开关 BGM
}

type Payload = Record<string, unknown> | undefined;

/**
 * VID-BRAND-1(2026-08-18 老板拍板):视频成片叠加**暂时不要二维码,只要 logo**。
 * 回开 = 改回 true —— QR 的默认摆放/拖拽/预览/持久化链路全部保留,关着时只是
 * 初始不自动摆 + 面板不露二维码字样。已存 overlayConfig 的老任务回显不受影响
 * (readInitialOverlay 的 stored 分支原样返回)。
 */
export const VIDEO_QR_OVERLAY_ENABLED = false;

export function readComposeMaterials(payload: Payload): ComposeMaterials {
  const vo = payload?.voiceovers;
  const hasVoice =
    Array.isArray(vo) && vo.some((s) => typeof s === "string" && s.trim().length > 0);
  const hasBgm = payload?.bgmId != null || typeof payload?.customBgmObjectKey === "string";
  return { hasVoice, hasBgm };
}

/** 回显当前 3 开关;缺省 = 开(对齐后端 `x is not False`)。 */
export function readInitialToggles(payload: Payload): ComposeToggles {
  const b = (k: string) => (typeof payload?.[k] === "boolean" ? (payload[k] as boolean) : true);
  return {
    voiceEnabled: b("voiceEnabled"),
    subtitleEnabled: b("subtitleEnabled"),
    bgmEnabled: b("bgmEnabled"),
  };
}

/** overlay 拖拽底图:成片缩略图 > 列表封面缩图 > payload.firstFrameUrl。 */
export function pickBaseImage(
  // ★task 可空:详情页要在「加载中」早返回之前算它(useComposeEditor 的入参),
  //   那一刻 task 还没到。没 task 就没底图,返回 null —— 调用方本来就按 null 判「能不能编辑」。
  task: VideoTaskDto | null,
  finalAsset: VideoAssetDto | null
): string | null {
  const pf = task?.requestPayload?.firstFrameUrl;
  return finalAsset?.thumbUrl ?? task?.coverThumbUrl ?? (typeof pf === "string" ? pf : null) ?? null;
}

/** 初始 overlay:优先回显已存 V6 config;否则用租户默认 logo/QR 摆默认角标位。 */
export function readInitialOverlay(
  payload: Payload,
  brand: BrandAssets | undefined
): OverlayConfig {
  const stored = payload?.overlayConfig;
  if (stored && typeof stored === "object" && (stored as OverlayConfig).version === 1) {
    return stored as OverlayConfig;
  }
  const logo = pickDefault(brand?.logos);
  // VID-BRAND-1:开关关着 → 新任务初始不摆 QR(qr=null 走下面各字段的空分支)。
  const qr = VIDEO_QR_OVERLAY_ENABLED ? pickDefault(brand?.qrs) : null;
  return {
    version: 1,
    logo: logo ? DEFAULT_OVERLAY.logo : null,
    qr: qr ? DEFAULT_OVERLAY.qr : null,
    logoUploadId: logo?.objectKey ?? null,
    qrCodeMode: qr ? qr.mode : null,
    qrCodeUploadId: qr && qr.mode === "UPLOAD" ? (qr.objectKey ?? null) : null,
    qrCodeUrl: qr && qr.mode === "URL" ? (qr.url ?? null) : null,
  };
}

/** 拼 VideoComposeOptions:无素材的开关发 undefined(JSON 丢弃 → 后端保持原值);overlay 始终发(含清空)。 */
export function buildComposeOptions(
  toggles: ComposeToggles,
  materials: ComposeMaterials,
  overlay: OverlayConfig
): VideoComposeOptions {
  return {
    voiceEnabled: materials.hasVoice ? toggles.voiceEnabled : undefined,
    subtitleEnabled: materials.hasVoice ? toggles.subtitleEnabled : undefined,
    bgmEnabled: materials.hasBgm ? toggles.bgmEnabled : undefined,
    overlayConfig: overlay,
  };
}

/**
 * VID-BRAND-1:多 logo 横向缩略图选择 —— 换 logoUploadId;logo 层还没开(null)时
 * 顺手按默认角标位把层打开(用户点了缩略图就是想要它上片),已拖过的位置保留。
 */
export function applyLogoSelection(overlay: OverlayConfig, logoObjectKey: string): OverlayConfig {
  return {
    ...overlay,
    logo: overlay.logo ?? DEFAULT_OVERLAY.logo,
    logoUploadId: logoObjectKey,
  };
}

/** DragOverlayCanvas 预览 logoUrl:overlay.logoUploadId 命中 brand.logos[].signedUrl。 */
export function resolveLogoPreviewUrl(
  overlay: OverlayConfig,
  brand: BrandAssets | undefined
): string | null {
  if (!overlay.logoUploadId || !brand) return null;
  return brand.logos.find((l) => l.objectKey === overlay.logoUploadId)?.signedUrl ?? null;
}

/** DragOverlayCanvas 预览 qrUrl:UPLOAD override 命中 signedUrl;URL 模式本期不预览。 */
export function resolveQrPreviewUrl(
  overlay: OverlayConfig,
  brand: BrandAssets | undefined
): string | null {
  if (overlay.qrCodeMode === "UPLOAD" && overlay.qrCodeUploadId && brand) {
    return brand.qrs.find((q) => q.objectKey === overlay.qrCodeUploadId)?.signedUrl ?? null;
  }
  return null;
}

export interface ComposedTag {
  key: "voice" | "subtitle" | "bgm" | "logo" | "qr";
  label: string;
}

/**
 * 当前成片实际包含哪些层 —— 读持久化的 recompose 选项(回显逻辑同面板),给下载区显示准确角标。
 * 未 recompose 过的片按默认:有口播文案=配音+字幕,有 BGM=BGM,无 overlay=无叠加。
 */
export function summarizeComposed(payload: Payload): ComposedTag[] {
  const m = readComposeMaterials(payload);
  const t = readInitialToggles(payload);
  const ov = (payload?.overlayConfig as OverlayConfig | undefined) ?? null;
  const tags: ComposedTag[] = [];
  if (m.hasVoice && t.voiceEnabled) tags.push({ key: "voice", label: "配音" });
  if (m.hasVoice && t.subtitleEnabled) tags.push({ key: "subtitle", label: "字幕" });
  if (m.hasBgm && t.bgmEnabled) tags.push({ key: "bgm", label: "背景音乐" });
  if (ov?.logo) tags.push({ key: "logo", label: "Logo" });
  if (ov?.qr) tags.push({ key: "qr", label: "二维码" });
  return tags;
}

/** 两个 compose 态(开关+overlay)是否一致 —— 面板判断「有无未应用改动」。 */
export function composeStateEqual(
  a: { toggles: ComposeToggles; overlay: OverlayConfig | null },
  b: { toggles: ComposeToggles; overlay: OverlayConfig | null }
): boolean {
  return (
    JSON.stringify(a.toggles) === JSON.stringify(b.toggles) &&
    JSON.stringify(a.overlay) === JSON.stringify(b.overlay)
  );
}
